/**
 * <torras-waitlist-popup> —— 新机预热收单弹窗
 *
 * 提交打到 Cloudflare Worker（services/waitlist-worker），由它建/找 Shopify 客户
 * 并打 preorder: 前缀的 tag。Admin token 全程不在前端。
 *
 * 四种打开方式，任选：
 *   1. 任意元素加属性        <button data-torras-waitlist>Join</button>
 *   2. 选择器                <torras-waitlist-popup open-on=".js-waitlist">
 *   3. JS API                TorrasWaitlist.open()
 *   4. 事件                  document.dispatchEvent(new Event('torras:waitlist:open'))
 * 另有自动触发（停留 / 滚动 / 退出意图），靠属性开关，默认全关。
 *
 * 用 Shadow DOM 隔离样式——主题全局 CSS 很厚，不隔离迟早被串味。
 * 但 CSS 自定义属性能穿透 shadow 边界，所以 --torras-forms-background-color
 * 这类品牌变量仍然可以从外面覆写。@font-face 是文档级的，FT Terra 照常继承。
 *
 * 抛出的事件（都冒泡、composed，方便埋点接）：
 *   torras:waitlist:opened     { trigger }
 *   torras:waitlist:submitted  { created, tags }
 *   torras:waitlist:dismissed  { stage }
 */
(() => {
  const CSS = `
:host{
  --tw-bg: var(--torras-forms-background-color, #f6e7d5);
  /* 渐变取自品牌参考图（顶部淡黄 / 右侧蜜桃 / 底部淡蓝，压在奶油底上）。
     参考图是 2.6:1 横幅，这里按竖版弹窗重排过——照搬横版的坐标会让蓝色糊到中间。
     整块可用 --torras-forms-background-image 覆盖或置 none 退回纯色。 */
  --tw-bg-image: var(--torras-forms-background-image,
    radial-gradient(130% 52% at 50% -6%,   #FCF8D6 0%, rgba(252,248,214,0) 60%),
    radial-gradient(78% 42% at 108% 40%,   #FDD8B4 0%, rgba(253,216,180,0) 66%),
    radial-gradient(120% 42% at 50% 106%,  #D5E9F2 0%, rgba(213,233,242,0) 64%),
    linear-gradient(180deg, var(--torras-forms-background-color, #f6e7d5) 0%,
                            #FBF3E9 30%, #FAF3EA 66%, #F3F0EA 100%));
  --tw-field:#FFFDFA;
  --tw-ink:#241B12;
  --tw-ink-2:#6B5A47;
  --tw-ink-3:#6F5C4A;
  --tw-line:#e6d5c1;
  --tw-mark:#C9B49C;
  --tw-primary: var(--torras-orange, #FF5B00);
  --tw-primary-hover: var(--torras-orange-hover, #E54E00);
  --tw-radius:.625rem;
  --tw-radius-md:calc(var(--tw-radius) - 2px);
  --tw-radius-sm:calc(var(--tw-radius) - 4px);
  --tw-font: var(--font-brand, 'FT Terra', ui-sans-serif, system-ui, sans-serif);
}
*{box-sizing:border-box}

dialog{
  width:100%;max-width:500px;padding:0;border:1px solid var(--tw-line);
  border-radius:var(--tw-radius);background:var(--tw-bg-image), var(--tw-bg);color:var(--tw-ink);
  font-family:var(--tw-font);font-size:14px;line-height:1.45;
  box-shadow:0 10px 15px -3px rgba(0,0,0,.1),0 4px 6px -4px rgba(0,0,0,.1);
  opacity:0;scale:.96;transition:opacity .2s ease,scale .2s ease,overlay .2s allow-discrete,display .2s allow-discrete;
}
dialog[open]{opacity:1;scale:1}
@starting-style{ dialog[open]{opacity:0;scale:.96} }
dialog::backdrop{background:rgba(0,0,0,.5);opacity:0;transition:opacity .2s ease,overlay .2s allow-discrete,display .2s allow-discrete}
dialog[open]::backdrop{opacity:1}
@starting-style{ dialog[open]::backdrop{opacity:0} }

.wrap{padding:22px 22px 26px;position:relative}  /* 底部比顶部多 6px：收尾是细字，等距会显得贴边 */
.close{position:absolute;top:16px;right:16px;width:24px;height:24px;padding:0;display:flex;
  align-items:center;justify-content:center;background:none;border:0;border-radius:var(--tw-radius-sm);
  color:var(--tw-ink-2);cursor:pointer;opacity:.7;transition:opacity .15s,background-color .15s}
.close:hover{opacity:1;background:rgba(120,85,45,.10)}
.close svg{width:16px;height:16px}

h2{font-size:19px;font-weight:600;letter-spacing:-.02em;line-height:1.25;margin:0 28px 0 0}
.desc{font-size:14px;color:var(--tw-ink-2);margin:6px 0 0}
.perks{list-style:none;display:flex;flex-wrap:wrap;gap:6px 16px;margin:14px 0 0;padding:0}
.perks li{display:flex;align-items:center;gap:6px;font-size:12.5px;color:var(--tw-ink-2)}
.perks svg{width:12px;height:12px;color:var(--tw-primary);flex:0 0 auto}

form{margin:20px 0 0}
/* 间距节奏：组内 8px < 组间 24px（3 倍差）。
   两者拉不开距离时，标签会读成"浮在两组之间"而不是归属下面那组。
   🔴 legend 在 display:flex 的 fieldset 里不是 flex item（Chrome 特殊处理），
   gap 够不着它，必须显式写 margin-bottom，否则标签会紧贴选项。 */
fieldset{border:0;margin:0 0 20px;padding:0;display:flex;flex-direction:column;gap:8px}
legend{padding:0;margin:0 0 8px;font-size:14px;font-weight:500;line-height:1;display:flex;align-items:center;gap:8px}
.badge{display:inline-flex;align-items:center;height:20px;padding:0 8px;border-radius:var(--tw-radius-sm);
  background:rgba(120,85,45,.10);color:var(--tw-ink-2);font-size:11.5px;font-weight:500}

/* 列数走变量，桌面 --cols / 移动 --cols-m，各组自己定。
   🔴 别用 grid-template-columns:1fr!important 一刀切移动端：
   带 grid-column:span 2 的选项会在单列网格里撑出一个隐式第二列，
   排出宽窄不齐的假两列（实测踩过）。 */
.opts{display:grid;gap:8px;grid-template-columns:repeat(var(--cols,2),minmax(0,1fr))}
.opt[style*='--span']{grid-column:span var(--span)}
.opt{position:relative;display:flex;align-items:center;gap:8px;padding:9px 11px;cursor:pointer;
  border:1px solid var(--tw-line);border-radius:var(--tw-radius-md);background:var(--tw-field);
  box-shadow:0 1px 2px rgba(0,0,0,.03);transition:background-color .15s,border-color .15s}
.opt:hover{background:#fffdfa;border-color:#d9c4ac}
.opt.rich{align-items:flex-start}
.opt input{position:absolute;opacity:0;width:0;height:0}
.mark{flex:0 0 auto;width:16px;height:16px;border:1px solid var(--tw-mark);background:var(--tw-field);
  display:flex;align-items:center;justify-content:center;box-shadow:0 1px 2px rgba(0,0,0,.03);
  transition:background-color .15s,border-color .15s}
.mark.radio{border-radius:50%}
.mark.check{border-radius:var(--tw-radius-sm);color:#fff}
.mark i{width:7px;height:7px;border-radius:50%;background:var(--tw-primary);scale:0;transition:scale .15s}
.mark svg{width:10px;height:10px;opacity:0;transition:opacity .12s}
.opt.rich .mark{margin-top:2px}
.body{display:flex;flex-direction:column;gap:1px;min-width:0}
.label{font-size:13.5px;line-height:1.2;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.sub{font-size:11.5px;line-height:1.3;color:var(--tw-ink-2);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.opt input:checked ~ .mark.radio{border-color:var(--tw-primary)}
.opt input:checked ~ .mark.radio i{scale:1}
.opt input:checked ~ .mark.check{background:var(--tw-primary);border-color:var(--tw-primary)}
.opt input:checked ~ .mark.check svg{opacity:1}
.opt:has(input:checked){border-color:var(--tw-primary);background:rgba(255,91,0,.04)}
.opt:has(input:focus-visible){border-color:var(--tw-primary);box-shadow:0 0 0 3px rgba(255,91,0,.45)}

.sep{height:1px;background:var(--tw-line);margin:0 0 20px}
input[type=email]{width:100%;height:36px;padding:0 12px;font:inherit;font-size:14px;color:var(--tw-ink);
  background:var(--tw-field);border:1px solid var(--tw-line);border-radius:var(--tw-radius-md);
  box-shadow:0 1px 2px rgba(0,0,0,.03);transition:border-color .15s,box-shadow .15s}
input[type=email]::placeholder{color:var(--tw-ink-3)}
input[type=email]:focus{outline:none;border-color:var(--tw-primary);box-shadow:0 0 0 3px rgba(255,91,0,.45)}
input[type=email][aria-invalid=true]{border-color:#ef4444;box-shadow:0 0 0 3px rgba(239,68,68,.18)}
.msg{font-size:12.5px;color:#ef4444;margin:0;display:none}
.msg.on{display:block}

/* 🔴 整行必须是 label。之前这里是 <div> + 只包住文字的 <label for>，
   结果视觉上那个方框（.mark）压根不在可点区域里——用户去点方框，没反应。
   选项行(.opt)从一开始就是整行 label，所以那边一直是好的。 */
.consent{display:flex;align-items:flex-start;gap:8px;margin:-8px 0 16px;cursor:pointer}
.consent input{position:absolute;opacity:0;width:0;height:0}
.consent .ct{font-size:13px;line-height:1.45;color:var(--tw-ink-2)}
.consent .mark{margin-top:1px;flex:0 0 auto}
/* 勾上时套用与选项行一致的选中态 */
.consent input:checked ~ .mark.check{background:var(--tw-primary);border-color:var(--tw-primary)}
.consent input:checked ~ .mark.check svg{opacity:1}
.consent input:focus-visible ~ .mark{box-shadow:0 0 0 3px rgba(255,91,0,.45)}
.consent.is-bad .mark{border-color:#D92D20}
.consent.is-bad .ct{color:#B42318}

.submit{width:100%;height:40px;font:inherit;font-size:14px;font-weight:500;color:#fff;
  background:var(--tw-primary);border:1px solid var(--tw-primary);border-radius:var(--tw-radius-md);
  cursor:pointer;box-shadow:0 1px 2px rgba(0,0,0,.05);transition:background-color .15s}
.submit:hover{background:var(--tw-primary-hover)}
.submit:disabled{opacity:.6;cursor:default}
.foot{display:flex;align-items:center;justify-content:space-between;gap:12px;margin:14px 0 0}
.fine{font-size:12px;color:var(--tw-ink-3);margin:0}
.ghost{font:inherit;font-size:12.5px;font-weight:500;color:var(--tw-ink-3);background:none;border:0;
  padding:4px 8px;margin-right:-8px;border-radius:var(--tw-radius-sm);cursor:pointer;transition:background-color .15s,color .15s}
.ghost:hover{background:rgba(120,85,45,.10);color:var(--tw-ink)}

.done{display:none;padding:36px 24px 32px;text-align:center}
:host([data-done]) .wrap{display:none}
:host([data-done]) .done{display:block}
.done-mark{width:40px;height:40px;margin:0 auto 14px;border-radius:50%;background:rgba(255,91,0,.12);
  color:var(--tw-primary);display:flex;align-items:center;justify-content:center}
.done-mark svg{width:18px;height:18px}
.done h2{margin:0 0 6px}
.done p{font-size:14px;color:var(--tw-ink-2);margin:0 0 20px}
.done p b{color:var(--tw-ink);font-weight:500}
.done a{display:inline-flex;align-items:center;justify-content:center;height:36px;padding:0 16px;
  font-size:14px;font-weight:500;color:#fff;background:var(--tw-primary);border-radius:var(--tw-radius-md);text-decoration:none}
.done a:hover{background:var(--tw-primary-hover)}

@media (max-width:520px){
  /* 底部抽屉 + 封顶 78dvh：内容再长也只占屏幕 78%，超出部分内部滚动。
     不封顶的话表单一长就顶到状态栏，用户以为整页被劫持了。 */
  /* 🔴 iOS Safari：<dialog> 贴的是「布局视口」底部，而 Safari 的上下工具栏是
     **盖在布局视口之上**的——所以贴底的抽屉，最下面那 80~90px（正好是同意行+按钮）
     会藏在工具栏底下，用户只看到邮箱框，以为按钮没了。
     dvh 解决不了：它管高度，不管锚点。只能靠 VisualViewport 量出真实可视区，
     用 --tw-vvb 把抽屉往上顶。拿不到就退回 0，跟以前一样。 */
  dialog{max-width:none;width:100%;margin:auto auto 0;overflow:auto;
    max-height:calc(var(--tw-vvh, 88dvh) * .88);
    margin-bottom:var(--tw-vvb, 0px);
    border-radius:var(--tw-radius) var(--tw-radius) 0 0;border-bottom:0;
    overscroll-behavior:contain;-webkit-overflow-scrolling:touch}
  /* 内容再长也保证 CTA 可见：按钮吸在滚动区底部 */
  .submit{position:sticky;bottom:0;z-index:2}
  form::after{content:'';display:block;height:2px}
  .opts{grid-template-columns:repeat(var(--cols-m,2),minmax(0,1fr))}
  .opt[style*='--span']{grid-column:span var(--span-m)}
  .opt{padding:8px 10px}
  .wrap{padding:22px 20px 26px}
}
@media (prefers-reduced-motion:reduce){
  dialog,dialog::backdrop,.opt,.mark,.mark i,.submit{transition:none!important}
  dialog{scale:1}
}
`;

  const DEFAULTS = {
    heading: 'Be first for iPhone 2026.',
    description: "Answer 3 quick questions — we'll email you a surprise code.",
    questions: [
      {
        id: 'model', label: 'Which one are you getting?', type: 'single', cols: 3, colsMobile: 3,
        options: [
          { value: 'standard', label: 'Standard' },
          { value: 'pro', label: 'Pro' },
          { value: 'pro-max', label: 'Pro Max' },
          { value: 'fold', label: 'Fold' },
          { value: 'undecided', label: 'Still deciding', span: 2, spanMobile: 2 },
        ],
      },
      {
        id: 'need', label: 'What should your protector do best?', type: 'single', cols: 2, colsMobile: 2,
        options: [
          { value: 'drop', label: 'Max drop protection', sub: '12 ft drop tested' },
          { value: 'anti-glare', label: 'Anti-reflective', sub: '7-layer AR coating' },
          { value: 'privacy', label: 'Privacy', sub: '25° viewing angle' },
          { value: 'hd', label: 'Crystal clarity', sub: '97% light transmission' },
        ],
      },
      {
        id: 'also', label: 'Anything else for it?', badge: 'Optional', type: 'multi', cols: 3, colsMobile: 3,
        options: [
          { value: 'case', label: 'Case' },
          { value: 'charging', label: 'Charging' },
          { value: 'lens', label: 'Lens' },
        ],
      },
    ],
    emailLabel: 'Send my code to',
    emailPlaceholder: 'Email address',
    consentLabel: 'Email me my code and launch updates.',
    cta: 'Claim my surprise code',
    finePrint: 'Unsubscribe anytime.',
    dismissLabel: 'No thanks',
    requiredId: 'model',
    consentError: 'Tick the box so we can email your code.',
    successIcon: true,
    alreadyHeading: "You're already on the list.",
    alreadyBody: 'We\'ll email <b>{email}</b> when the code goes live.',
    successHeading: 'Your code is on the way.',
    successBody: 'Sent to <b>{email}</b>.',
    successCta: 'Shop current models',
    successCtaUrl: '/collections/screen-protectors',
  };

  const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i;
  const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const CHECK = '<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M1.5 6.2l3 3 6-6.4"/></svg>';

  class TorrasWaitlistPopup extends HTMLElement {
    #cfg; #root; #dialog; #form; #email; #consent; #err; #submit; #ctaLabel; #lastTrigger = null;
  #busy = false; #lastAttempt = 0;

    connectedCallback() {
      if (this.#root) return;
      // 配置在子元素里。如果脚本比 HTML 先跑到（async / 手动 define / 早期注入），
      // 自定义元素会在子节点解析完之前就 upgrade，读到空配置然后静默退回默认文案——
      // 线上表现是"运营在 Theme Editor 改的字不生效"，极难查。等解析完再初始化。
      if (!this.querySelector('script[type="application/json"]') && document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.connectedCallback(), { once: true });
        return;
      }
      this.#cfg = { ...DEFAULTS, ...this.#readConfig() };
      this.#root = this.attachShadow({ mode: 'open' });
      this.#root.innerHTML = `<style>${CSS}</style>${this.#markup()}`;
      this.#bind();
      this.#armTriggers();
      TorrasWaitlist._register(this);
    }

    /** 配置从子元素的 JSON 读，没有就用内置默认值。Liquid 侧直接把 blocks 渲成这段 JSON。 */
    #readConfig() {
      const el = this.querySelector('script[type="application/json"]');
      if (!el) return {};
      try { return JSON.parse(el.textContent); }
      catch (e) { console.warn('[torras-waitlist] 配置 JSON 解析失败，回退默认值', e); return {}; }
    }

    #markup() {
      const c = this.#cfg;
      const questions = (c.questions || []).map((q) => {
        const multi = q.type === 'multi';
        const opts = (q.options || []).map((o) => `
          <label class="opt${o.sub ? ' rich' : ''}"${o.span ? ` style="--span:${o.span};--span-m:${o.spanMobile || o.span}"` : ''}>
            <input type="${multi ? 'checkbox' : 'radio'}" name="${esc(q.id)}" value="${esc(o.value)}">
            <span class="mark ${multi ? 'check' : 'radio'}">${multi ? CHECK : '<i></i>'}</span>
            <span class="body"><span class="label">${esc(o.label)}</span>${o.sub ? `<span class="sub">${esc(o.sub)}</span>` : ''}</span>
          </label>`).join('');
        return `<fieldset>
            <legend>${esc(q.label)}${q.badge ? `<span class="badge">${esc(q.badge)}</span>` : ''}</legend>
            <div class="opts" style="--cols:${q.cols || 2};--cols-m:${q.colsMobile || Math.min(q.cols || 2, 2)}">${opts}</div>
          </fieldset>`;
      }).join('');

      return `
<dialog part="dialog">
  <div class="wrap">
    <button class="close" type="button" data-close aria-label="Close">
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M2.5 2.5l11 11M13.5 2.5l-11 11"/></svg>
    </button>
    <h2>${esc(c.heading)}</h2>
    ${c.description ? `<p class="desc">${esc(c.description)}</p>` : ''}
    <form novalidate>
      ${questions}
      <fieldset>
        <input type="email" id="tw-email" name="email" placeholder="${esc(c.emailPlaceholder)}"
               autocomplete="email" inputmode="email" spellcheck="false">
        <p class="msg"></p>
      </fieldset>
      <label class="consent" for="tw-consent">
        <input type="checkbox" id="tw-consent" name="consent">
        <span class="mark check">${CHECK}</span>
        <span class="ct">${esc(c.consentLabel)}</span>
      </label>
      <input type="text" name="hp" tabindex="-1" autocomplete="off" aria-hidden="true"
             style="position:absolute;left:-9999px;width:1px;height:1px">
      <button class="submit" type="submit">${esc(c.cta)}</button>
      <div class="foot">
        <p class="fine">${esc(c.finePrint)}</p>
        <button class="ghost" type="button" data-close>${esc(c.dismissLabel)}</button>
      </div>
    </form>
  </div>
  <div class="done">
    ${c.successIcon === false ? '' : '<div class="done-mark"><svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3.5 10.5l4 4 9-9.5"/></svg></div>'}
    <h2>${esc(c.successHeading)}</h2>
    <p data-success-body></p>
    ${c.successCta ? `<a href="${esc(c.successCtaUrl)}">${esc(c.successCta)}</a>` : ''}
  </div>
</dialog>`;
    }

    #bind() {
      const $ = (s) => this.#root.querySelector(s);
      this.#dialog = $('dialog');
      this.#form = $('form');
      this.#email = $('input[type=email]');
      this.#consent = $('#tw-consent');
      this.#err = $('.msg');
      this.#submit = $('.submit');
      this.#ctaLabel = this.#submit.textContent;

      this.#root.querySelectorAll('[data-close]').forEach((b) =>
        b.addEventListener('click', () => this.close('dismissed')));

      // 点击 backdrop 关闭：原生 dialog 的 backdrop 点击落在 dialog 元素本身上
      this.#dialog.addEventListener('click', (e) => {
        if (e.target === this.#dialog) this.close('dismissed');
      });
      this.#dialog.addEventListener('cancel', () => this.#remember('dismissed'));  // ESC
      this.#email.addEventListener('input', () => { if (this.#err.classList.contains('on')) this.#validate(); });
      this.#consent.addEventListener('change', () => { if (this.#err.classList.contains('on')) this.#validate(); });
      this.#form.addEventListener('submit', (e) => this.#onSubmit(e));
    }

    // ---- 触发器 ----
    #armTriggers() {
      const sel = this.getAttribute('open-on');
      if (sel) {
        document.addEventListener('click', (e) => {
          if (e.target.closest?.(sel)) { e.preventDefault(); this.open('selector'); }
        });
      }
      if (this.#cooling()) return;

      const delay = Number(this.getAttribute('auto-delay') || 0);
      if (delay > 0) setTimeout(() => this.open('delay'), delay * 1000);

      const pct = Number(this.getAttribute('auto-scroll') || 0);
      if (pct > 0) {
        const onScroll = () => {
          const d = document.documentElement;
          if (d.scrollTop / (d.scrollHeight - d.clientHeight || 1) * 100 >= pct) {
            window.removeEventListener('scroll', onScroll);
            this.open('scroll');
          }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
      }
      if (this.hasAttribute('exit-intent')) {
        document.addEventListener('mouseout', (e) => {
          if (!e.relatedTarget && e.clientY <= 0) this.open('exit-intent');
        });
      }
    }

    // ---- 冷却 ----
    get #key() { return this.getAttribute('storage-key') || 'torras_waitlist'; }
    #cooling() {
      const days = Number(this.getAttribute('cooldown-days') || 14);
      if (!days) return false;
      try {
        const ts = Number((localStorage.getItem(this.#key) || '').split(':')[1] || 0);
        return ts && Date.now() - ts < days * 864e5;
      } catch { return false; }
    }
    #remember(state, email) {
      try { localStorage.setItem(this.#key, `${state}:${Date.now()}:${email || ''}`); } catch {}
    }
    /** 读回上次状态。格式 "状态:时间戳:邮箱"——第三段是后加的，老数据没有也不会炸。 */
    #stored() {
      try {
        const [state = '', ts = '', ...rest] = (localStorage.getItem(this.#key) || '').split(':');
        return { state, ts: Number(ts) || 0, email: rest.join(':') };
      } catch { return { state: '', ts: 0, email: '' }; }
    }

    /** 把 VisualViewport 的真实可视高度 / 底部被遮住的高度写成 CSS 变量。
        自定义属性能穿透 Shadow DOM，所以在宿主上设一次就够。 */
    #syncViewport = () => {
      const vv = window.visualViewport;
      if (!vv) return;
      const hidden = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      this.style.setProperty('--tw-vvh', `${vv.height}px`);
      this.style.setProperty('--tw-vvb', `${hidden}px`);
    };
    #bindViewport(on) {
      const vv = window.visualViewport;
      if (!vv) return;
      if (on) { this.#syncViewport(); vv.addEventListener('resize', this.#syncViewport); vv.addEventListener('scroll', this.#syncViewport); }
      else { vv.removeEventListener('resize', this.#syncViewport); vv.removeEventListener('scroll', this.#syncViewport); }
    }

    // ---- 开关 ----
    open(trigger = 'api') {
      if (this.#dialog.open) return;
      if (trigger !== 'api' && trigger !== 'selector' && trigger !== 'attribute' && this.#cooling()) return;
      this.#lastTrigger = document.activeElement;

      // 提交过的人再打开，直接显示「已在名单里」——否则又是一张空表单，
      // 用户会以为上次没成功而重复提交。
      const prev = this.#stored();
      if (prev.state === 'subscribed') this.#showAlready(prev.email);
      else this.removeAttribute('data-done');

      this.#bindViewport(true);
      this.#dialog.showModal();
      this.#emit('opened', { trigger });
    }
    close(reason = 'dismissed') {
      if (!this.#dialog.open) return;
      this.#bindViewport(false);
      this.#dialog.close();
      if (reason === 'dismissed') { this.#remember('dismissed'); this.#emit('dismissed', { stage: this.hasAttribute('data-done') ? 'success' : 'form' }); }
      this.#lastTrigger?.focus?.();
    }
    #emit(name, detail) {
      this.dispatchEvent(new CustomEvent(`torras:waitlist:${name}`, { detail, bubbles: true, composed: true }));
    }

    // ---- 提交 ----
    #value(id) {
      const nodes = this.#root.querySelectorAll(`input[name="${id}"]:checked`);
      const q = (this.#cfg.questions || []).find((x) => x.id === id);
      if (q?.type === 'multi') return [...nodes].map((n) => n.value);
      return nodes[0]?.value || '';
    }
    #validate() {
      const okMail = EMAIL_RE.test(this.#email.value.trim());
      const req = this.#cfg.requiredId;
      const okReq = !req || !!this.#value(req)?.length;
      // 勾同意是硬门槛：Worker 只在 consent 为真时才写 Shopify 的
      // emailMarketingConsent(SUBSCRIBED) 并 subscribe 进 Klaviyo。不勾就放行的话，
      // 人是留下了、tag 也打了，但收不到码——弹窗承诺的那件事直接落空。
      const needConsent = this.hasAttribute('consent-required');
      const okConsent = !needConsent || this.#consent.checked;

      // 报错按「先补哪个」的顺序给，一次只说一件事
      let msg = '';
      if (!okReq) msg = 'Pick an option first.';
      else if (!okMail) msg = 'Enter a valid email address.';
      else if (!okConsent) msg = this.#cfg.consentError;
      this.#err.textContent = msg;
      this.#err.classList.toggle('on', !!msg);

      this.#email.setAttribute('aria-invalid', okMail ? 'false' : 'true');
      this.#consent.setAttribute('aria-invalid', okConsent ? 'false' : 'true');
      this.#root.querySelector('.consent')?.classList.toggle('is-bad', !okConsent);
      return okMail && okReq && okConsent;
    }
    async #onSubmit(e) {
      e.preventDefault();

      // 三道闸，缺一不可：
      // ① #busy —— 唯一可靠的一道。禁用按钮挡不住回车提交，也挡不住
      //    两次点击落在同一帧里（disabled 生效前第二次已经进来了）。
      // ② 1 秒节流 —— 失败后疯狂重试会把 worker 的 IP 限流打满，
      //    真实用户反而被自己锁在门外。
      // ③ 12 秒超时 —— 没有它，请求挂住时按钮永远停在 Sending…，用户只能刷新。
      if (this.#busy) return;
      const now = Date.now();
      if (now - this.#lastAttempt < 1000) return;
      this.#lastAttempt = now;

      if (!this.#validate()) return;

      const payload = { email: this.#email.value.trim(), consent: this.#consent.checked,
                        hp: this.#root.querySelector('input[name=hp]').value };
      for (const q of this.#cfg.questions || []) payload[q.id] = this.#value(q.id);

      const endpoint = this.getAttribute('endpoint');
      if (!endpoint) { this.#succeed({ created: null, tags: [] }, payload); return; }  // 无端点 = 演示模式

      this.#busy = true;
      this.#submit.disabled = true;
      this.#submit.textContent = this.getAttribute('sending-label') || 'Sending…';

      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), Number(this.getAttribute('timeout-ms') || 12000));
      try {
        const res = await fetch(`${endpoint.replace(/\/$/, '')}/subscribe`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload), signal: ctrl.signal, keepalive: true,
        });
        const out = await res.json().catch(() => ({}));
        if (out.ok) this.#succeed(out, payload);
        else {
          this.#err.textContent = out.error === 'rate_limited'
            ? 'Too many tries — give it a minute.' : 'Something went wrong. Try again in a moment.';
          this.#err.classList.add('on');
        }
      } catch (err) {
        this.#err.textContent = err?.name === 'AbortError'
          ? 'That took too long. Try again.' : 'Network hiccup. Try again in a moment.';
        this.#err.classList.add('on');
      } finally {
        clearTimeout(timer);
        this.#busy = false;
        this.#submit.disabled = false;
        this.#submit.textContent = this.#ctaLabel;
      }
    }
    /** 复用成功面板，只换文案——不新增 DOM，样式天然一致。 */
    #showAlready(email) {
      const c = this.#cfg;
      const h = this.#root.querySelector('.done h2');
      const body = this.#root.querySelector('[data-success-body]');
      if (h) h.textContent = c.alreadyHeading;
      if (body) body.innerHTML = String(c.alreadyBody).replace('{email}', email ? esc(email) : 'you');
      this.setAttribute('data-done', '');
    }

    #succeed(out, payload) {
      const body = this.#root.querySelector('[data-success-body]');
      body.innerHTML = String(this.#cfg.successBody).replace('{email}', esc(payload.email));
      this.setAttribute('data-done', '');
      this.#remember('subscribed', payload.email);
      this.#emit('submitted', { created: out.created, tags: out.tags || [] });
      this.#klaviyo(payload);
    }

    /**
     * 直接把人加进 Klaviyo 的专用 list（只用公钥，Klaviyo 自家表单走的就是这个接口）。
     *
     * 🔴 为什么必须在这儿发，而不能只靠 Shopify→Klaviyo 同步：
     *    ① 老 profile 的 `Shopify Tags` 同步时**不刷新** —— 回头客身上永远是旧值，
     *       任何基于这个 tag 的过滤/分群都判不中他们。
     *    ② 老订阅者**早就在 Email List 里**，不会再触发一次「加入」，
     *       list 触发的 flow 对他们完全不响。
     *    实测（2026-08-28）：同一邮箱 Shopify 侧 tag 正确、profile 也被更新了，
     *    但 tag 仍是入库时的旧值、也没进新 list —— 两条路都堵死。
     *
     * 发完即走：不 await、不挡成功页；失败静默 —— Shopify 那边的 tag 已经写好，
     * 人不会丢，最坏情况是这封信没发出去，可用 campaign 兜底。
     */
    #klaviyo(payload) {
      const key = this.getAttribute('klaviyo-key');
      const list = this.getAttribute('klaviyo-list');
      if (!key || !list) return;                     // 没配 = 不发，默认关闭

      const props = { };
      for (const q of this.#cfg.questions || []) {
        const raw = payload[q.id];
        if (raw == null || raw === '' || (Array.isArray(raw) && !raw.length)) continue;
        const vals = Array.isArray(raw) ? raw : [raw];
        const labelOf = (v) => (q.options || []).find((o) => o.value === v)?.label || v;
        props[`preorder_${q.id}_key`] = vals.join(', ');
        props[`preorder_${q.id}`] = vals.map(labelOf).join(', ');
      }

      const body = JSON.stringify({ data: { type: 'subscription', attributes: {
        custom_source: this.getAttribute('klaviyo-source') || 'Waitlist popup',
        profile: { data: { type: 'profile', attributes: { email: payload.email, properties: props } } },
      }, relationships: { list: { data: { type: 'list', id: list } } } } });

      try {
        fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(key)}`, {
          method: 'POST', mode: 'cors', keepalive: true,
          headers: { 'Content-Type': 'application/json', revision: '2025-07-15' },
          body,
        }).catch(() => {});
      } catch (_) { /* 静默：Shopify 侧已记录 */ }
    }
  }

  /** 全局门面：TorrasWaitlist.open() / 属性触发 / 事件触发 */
  const TorrasWaitlist = {
    _instances: new Set(),
    _register(el) { this._instances.add(el); },
    _pick(name) {
      if (!name) return [...this._instances][0];
      return [...this._instances].find((el) => el.getAttribute('name') === name) || [...this._instances][0];
    },
    open(name) { this._pick(name)?.open('api'); },
    close(name) { this._pick(name)?.close('api'); },
  };

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest?.('[data-torras-waitlist]');
    if (!trigger) return;
    e.preventDefault();
    const el = TorrasWaitlist._pick(trigger.getAttribute('data-torras-waitlist') || null);
    el?.open('attribute');
  });
  document.addEventListener('torras:waitlist:open', (e) => TorrasWaitlist.open(e.detail?.name));

  window.TorrasWaitlist = TorrasWaitlist;
  if (!customElements.get('torras-waitlist-popup')) {
    customElements.define('torras-waitlist-popup', TorrasWaitlistPopup);
  }
})();
